import { z } from 'zod'
import { roundOddsSchema, type BirdieVenue, type RoundOdds } from './RoundOdds'
import type { HardClose } from './HardClose'

export interface RaceEvent {
  uuid: string
  raceId: string
  eventType: string
  eventTimestamp: string
}

export interface RaceSettledEvent extends RaceEvent {
  eventType: 'race_settled'
  payload: {
    results: {
      marbleId: string
      marbleNumber: number
      marbleColor: string
      position: number
    }[]
    // Optional CCTV-only field (irl-vehicle-counter games). Captures the
    // location the round was streamed from at result time, so the
    // widget's betting history can show "📍 Tokyo, Japan" alongside
    // settled bets. Absent for all other game types and for any rounds
    // that pre-dated the engine change. Always treat as optional.
    streamMetadata?: {
      location?: string
      streamKey?: string
      detectionType?: string
    }
    // CCTV final count for the round. Surfaced by the bet history so
    // a player who bet on an Exact boundary can see WHICH count
    // landed (e.g. "Outcome: 13" — bet on Exact 12, lost). Absent for
    // non-CCTV games and for any pre-deploy CCTV rounds.
    finalCount?: number
    // Per-market winners for chip-placement games with configured
    // side-bet markets (Even/Odd, Exact-Boundary, etc.). Keyed by
    // betting_market.id, value is the same shape as `results`. Markets
    // with no winner this round (e.g. Exact-Boundary on a non-jackpot
    // round) are omitted entirely — bet-history/render code should
    // fall through to "lost" when the bet's market key is absent.
    marketResults?: Record<
      string,
      {
        marbleId: string
        marbleNumber: number
        marbleColor: string
        position: number
      }[]
    >
    // Provable-fairness REVEAL — published by the game engine at settlement,
    // never earlier, so the seed can't be sniffed before the outcome is
    // locked. Together with the `serverSeedHash` commitment on that round's
    // race_ready event, anyone can recompute the round from public values.
    // snake_case keys as on the wire; the settled-event zod guard tolerates
    // (and the mappers preserve) the extra payload key. Absent for engines
    // that don't publish a reveal and for pre-reveal rounds — always treat
    // as optional.
    //
    // FOUR GAME FAMILIES share this envelope, discriminated by `game`:
    //   `crash` (absent `game` — 155-game-crash, the original shape):
    //       seed → HMAC → first 52 bits → multiplier curve reproduces
    //       crash_multiplier.
    //   `vehicle_boundaries` (irl-vehicle-counter counting/CCTV games):
    //       the clip's final_count is a MEASURED public input (not
    //       seed-derived); the seed picks the weighted outcome and places
    //       lower_bound/upper_bound around that count. `draws` is the
    //       label → 13-hex map the verifier compares verbatim.
    //   `marble_order` (classic marble races) and `birdie` (golf putting):
    //       fully seed-derived orderings — see their field groups below.
    //
    // `game` is the ONLY fairness discriminator — do NOT infer the family
    // from the widget's game-type predicates (`crash` deliberately sits
    // inside CCTV_GAME_TYPES for music suppression, so isCCTVGameType is
    // not a fairness signal). Absent `game` means crash, for back-compat
    // with every round the crash engine already published.
    fairness?: {
      game?: string
      server_seed?: string
      nonce?: string
      server_seed_hash?: string
      // ── crash family ──
      crash_multiplier?: number
      house_edge?: number
      // v2 hash-chain reveal (scheme_version >= 2). Absent on v1/legacy
      // rounds; `chain_index` is the round's position in the reverse chain,
      // `chain_root_hash` the published root every link folds back to, and
      // `beacon` the public drand entropy (empty for baked-clip crash).
      scheme_version?: number
      chain_root_hash?: string
      chain_index?: number
      // The crash engine emits an OBJECT ({round, randomness}); the counting
      // engine emits the STRING "" (its clips are baked, so there is no
      // beacon). Both are on the wire — normalise with
      // `resolveBeaconRandomness` before it reaches the fairness core, or a
      // non-empty string beacon silently derives the WRONG draw stream.
      beacon?: string | { round?: number; randomness?: string }
      // ── vehicle_boundaries (counting) family ──
      // What the seed decided …
      outcome?: string
      lower_bound?: number
      upper_bound?: number
      gap?: number
      // … and the distribution it decided it against. `outcomes` is in the
      // canonical order and `weights_ppm` is index-aligned to it, as integer
      // parts-per-million summing to 1_000_000.
      outcomes?: string[]
      weights_ppm?: number[]
      // label → 13-hex draw. EXACTLY two entries: "outcome" plus the one
      // branch label the outcome took.
      draws?: Record<string, string>
      // Public reveal INPUT — camera-measured, NOT seed-derived. A verifier
      // cannot reproduce the boundaries without it.
      final_count?: number
      // Binds the reveal to the specific clip/stream it was measured from.
      clip_id?: string
      stream_key?: string
      zone_name?: string
      // ── marble_order (marble race) family ──
      // Unlike the counting family, the finishing ORDER is fully seed-derived: a
      // single "order" draw picks it. `order` is the 1st..Kth finishers by
      // entrant name; `marbles` the canonical entrant universe; `k` the podium
      // size; `index` the chosen k-permutation (equal) or winner (weighted)
      // index; `draw` the single 13-hex draw. `weights_ppm` (reused from the
      // counting fields above) is present for WEIGHTED single-winner games only.
      order?: string[]
      marbles?: string[]
      k?: number
      index?: number
      draw?: string
      // ── birdie (golf putting) family ──
      // The PATTERN reveal is marble_order-shaped and reuses the group above:
      // one "order" draw over the eight H/M putt patterns, `weights_ppm`
      // present because the board is weighted. What Birdie adds is the CARD —
      // a second, domain-separated draw over the same seed picks which golfer
      // the round was priced from, so the difficulty is revealed as fairly as
      // the result. `card_draw` is that draw and `card_index` the entry it
      // chose out of the `catalogue_*` universe; `make_rate_ppm` is the card's
      // per-putt make chance and `paytable_hash` binds the prices that paid.
      // `clip_sha256` — with `clip_id` above, shared with the counting family —
      // binds the reveal to the exact putt footage that played.
      // Written by the engine's `build_birdie_reveal`; read defensively via
      // `BirdieBoard/pastRounds.readBirdieFairness`, because a declared key is
      // not a delivered one (an older engine simply omits the group).
      card_draw?: string
      card_index?: number
      catalogue_version?: number
      catalogue_hash?: string
      catalogue_size?: number
      make_rate_ppm?: number
      /** The base RTP the board was priced at (940000 since 2026-09-21; engines before that reveal did not publish it). */
      rtp_ppm?: number
      paytable_hash?: string
      clip_sha256?: string
      // The round-type side market (plain / frost / fire): its own
      // domain-separated draw over the same seed, settled against
      // `round_types_ppm`. All three travel together because the draw alone
      // cannot reproduce the type - `weighted_pick` needs the weights, and
      // `weights_ppm` above is the pattern table, not this one.
      //
      // This reveal is the SECOND time the widget hears the type. The first is
      // the live one: the engine repeats the same lowercase label on every
      // `putt_result` row (`BirdieBoard/livePutts.RevealedRoundType`), which is
      // why those rows are the only pre-settlement payload that names it —
      // they go out while the clip is already playing, seconds past the
      // backend's post-close bet-acceptance grace that `race_started` still
      // sits inside. `putt_result` rows never become `RaceEvent`s: they are
      // diverted into `store/birdiePutts` (see `livePutts`), so there is no
      // event interface for them here.
      round_type?: string
      round_type_draw?: string
      round_types_ppm?: Record<string, number>
    }
  }
}

// race_started payload. Originally CCTV-only (irl-vehicle-counter
// games); Birdie now adds `location` + `venue` on the same event, so
// every field is optional and each game reads only its own keys.
// For CCTV it carries the
// per-round numeric boundaries + per-option label strings on the
// race_started event — the moment betting is fully closed — so the
// chip-placement panel can render boundary labels on each option in
// place of the burned-in stream overlay. Absent for non-CCTV games
// and for any pre-deploy CCTV rounds — gate rendering on payload
// presence + isCCTVGameType().
//
// Deliberately tied to race_started (NOT race_starting) because
// race_starting is still within the betting countdown — exposing
// boundaries in that window would let players adjust their bet on
// information they shouldn't have until betting closes.
export interface RaceStartedEvent extends RaceEvent {
  eventType: 'race_started'
  payload?: {
    // CCTV games only — absent for Birdie, crash and the classics.
    boundaries?: {
      lower: number
      upper: number
    }
    // marbleId → human-readable label (e.g. "< 12", "12 – 14",
    // "> 14", "= 12 or = 14"). Server-formatted so the widget
    // doesn't need per-game label logic. CCTV games only.
    optionLabels?: Record<string, string>
    // CCTV and Birdie: the place the round is filmed, surfaced by the
    // toolbar pill and (Birdie) the golfer card once betting has closed.
    // Absent for non-CCTV games and pre-deploy CCTV rounds.
    location?: string
    // Birdie only — the green as an object for the card; never overload
    // `location`.
    venue?: BirdieVenue
  }
}

export interface RaceCancelledEvent extends RaceEvent {
  eventType: 'race_cancelled'
}

export interface RaceEndedEvent extends RaceEvent {
  eventType: 'race_ended'
  payload?: {
    crash_multiplier?: number
  }
}

export interface RaceCrashedEvent extends RaceEvent {
  eventType: 'race_crashed'
  payload: {
    crashMultiplier: number
  }
}

// race_ready opens the betting window. Crash games attach a
// provable-fairness commitment here — `serverSeedHash` is a sha256
// of the round's secret server seed AND its nonce ("seed:nonce"),
// published BEFORE betting so a player can record it and verify
// (after settlement, once both are revealed) that the round wasn't
// manipulated. Covering the nonce is what makes it bind the outcome
// rather than just the seed. Anon-safe (just a
// commitment hash). Absent for non-crash games and for any rounds
// that pre-dated the engine change — always treat as optional and
// gate rendering on its presence.
export interface RaceReadyEvent extends RaceEvent {
  eventType: 'race_ready'
  payload?: {
    serverSeedHash?: string
    // Dynamic-odds games (Birdie): the round's committed board. Absent for
    // every other game — gate the per-round override on its presence (A28).
    odds?: RoundOdds
    // The hard close (`HardClose.ts`): present only on a round whose engine
    // sent `bet_close: "hard"`. Absent: the round runs today's board.
    hardClose?: HardClose
  }
}

export interface RaceScheduledEvent extends RaceEvent {
  eventType: 'race_scheduled'
  payload?: {
    start_timestamp: string
  }
}

const raceEventSchema = z.object({
  uuid: z.string(),
  raceId: z.string(),
  eventType: z.string(),
  eventTimestamp: z.string()
})

const raceSettledEventSchema = raceEventSchema.extend({
  eventType: z.literal('race_settled'),
  payload: z.object({
    results: z.array(
      z.object({
        marbleId: z.string(),
        marbleNumber: z.number(),
        marbleColor: z.string(),
        position: z.number()
      })
    ),
    streamMetadata: z
      .object({
        location: z.string().optional(),
        streamKey: z.string().optional(),
        detectionType: z.string().optional()
      })
      .optional()
  })
})

const raceStartedEventSchema = raceEventSchema.extend({
  eventType: z.literal('race_started'),
  // Every field is optional and the object passes unknown keys through:
  // CCTV sends boundaries + optionLabels, Birdie sends location + venue
  // (+ the clip commitment), crash sends `{game_mode: "crash"}`. Requiring
  // any one of them would have failed the guard for the other games and
  // dropped their payload on the store round-trip.
  payload: z
    .object({
      boundaries: z.object({ lower: z.number(), upper: z.number() }).optional(),
      optionLabels: z.record(z.string()).optional(),
      location: z.string().optional(),
      venue: z.object({ id: z.string(), name: z.string() }).passthrough().optional()
    })
    .passthrough()
    .optional()
})

const raceCancelledEventSchema = raceEventSchema.extend({
  eventType: z.literal('race_cancelled')
})

const raceEndedEventSchema = raceEventSchema.extend({
  eventType: z.literal('race_ended'),
  payload: z
    .object({
      crash_multiplier: z.number().optional()
    })
    .optional()
})

const raceReadyEventSchema = raceEventSchema.extend({
  eventType: z.literal('race_ready'),
  // Optional provable-fairness commitment (crash games). `.passthrough()`
  // tolerates any other keys the engine may attach to race_ready without
  // failing the guard.
  payload: z
    .object({
      serverSeedHash: z.string().optional(),
      // Dynamic-odds games (Birdie) attach the round's committed board here.
      // One variant, not a second schema: a race_ready is a race_ready
      // whichever game produced it (A15).
      odds: roundOddsSchema.optional()
    })
    .passthrough()
    .optional()
})

const raceScheduledEventSchema = raceEventSchema.extend({
  eventType: z.literal('race_scheduled'),
  payload: z
    .object({
      start_timestamp: z.string()
    })
    .optional()
})

const raceCrashedEventSchema = raceEventSchema.extend({
  eventType: z.literal('race_crashed'),
  payload: z.object({
    crashMultiplier: z.number()
  })
})

export const isRaceSettledEvent = (event: RaceEvent): event is RaceSettledEvent => {
  return raceSettledEventSchema.safeParse(event).success
}

export const isRaceCancelledEvent = (event: RaceEvent): event is RaceCancelledEvent => {
  return raceCancelledEventSchema.safeParse(event).success
}

export const isRaceReadyEvent = (event: RaceEvent): event is RaceReadyEvent => {
  return raceReadyEventSchema.safeParse(event).success
}

export const isRaceScheduledEvent = (event: RaceEvent): event is RaceScheduledEvent => {
  return raceScheduledEventSchema.safeParse(event).success
}

export const isRaceEndedEvent = (event: RaceEvent): event is RaceEndedEvent => {
  return raceEndedEventSchema.safeParse(event).success
}

export const isRaceCrashedEvent = (event: RaceEvent): event is RaceCrashedEvent => {
  return raceCrashedEventSchema.safeParse(event).success
}

export const isRaceStartedEvent = (event: RaceEvent): event is RaceStartedEvent => {
  return raceStartedEventSchema.safeParse(event).success
}
